export const CANTON_REQUEST_PROVIDER_EVENT = "canton:requestProvider";
export const CANTON_ANNOUNCE_PROVIDER_EVENT = "canton:announceProvider";

const DEFAULT_DISCOVERY_TIMEOUT_MS = 300;

export interface AnnouncedCantonProvider {
  /** Stable identifier for the wallet/extension. */
  id: string;
  /** Display name for pickers. */
  name: string;
  /** Optional icon (data URL or https URL). */
  icon?: string;
  /** Routing key for the postMessage transport (defaults to `id`). */
  target?: string;
}

/**
 * CIP-103 browser-wallet discovery (EIP-6963-style).
 * The wallet dispatches `canton:announceProvider` and dApps
 * dispatch `canton:requestProvider` to ask installed wallets to re-announce.
 * Each announcement carries a `target` we route through Sigilry's `WindowTransport`,
 * so multiple extensions coexist without contending on the `window.canton` global.
 */
export async function discoverCantonProviders(
  timeoutMs: number = DEFAULT_DISCOVERY_TIMEOUT_MS,
  win: Window = window
): Promise<AnnouncedCantonProvider[]> {
  if (typeof win === "undefined") return [];

  const discovered = new Map<string, AnnouncedCantonProvider>();

  const handler = (event: Event) => {
    const detail = (event as CustomEvent).detail as
      | Partial<AnnouncedCantonProvider>
      | undefined;
    if (!detail?.id || !detail.name || discovered.has(detail.id)) return;
    discovered.set(detail.id, {
      id: detail.id,
      name: detail.name,
      icon: detail.icon,
      target: detail.target ?? detail.id,
    });
  };

  win.addEventListener(CANTON_ANNOUNCE_PROVIDER_EVENT, handler);
  try {
    win.dispatchEvent(
      new CustomEvent(CANTON_REQUEST_PROVIDER_EVENT, { detail: {} })
    );
    await new Promise((resolve) => setTimeout(resolve, timeoutMs));
  } finally {
    win.removeEventListener(CANTON_ANNOUNCE_PROVIDER_EVENT, handler);
  }

  return Array.from(discovered.values());
}
